import { useTranslation } from 'react-i18next'
import { Calendar } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { DrilldownAppointmentRow } from '@/types/analyticsDrilldown'
import { formatDateTime } from './formatters'

interface Props {
  rows: DrilldownAppointmentRow[]
  locale: string
  darkMode: boolean
}

const STATUS_TONE: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
  confirmed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300',
  cancelled: 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300',
  completed: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300',
}

export function AppointmentsDrilldownTable({ rows, locale, darkMode }: Props) {
  const { t } = useTranslation()
  const headerBase = cn(
    'px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide',
    darkMode ? 'text-slate-400' : 'text-slate-500',
  )
  const cellBase = cn(
    'px-3 py-3 text-sm',
    darkMode ? 'text-slate-200' : 'text-slate-700',
  )
  const rowBorder = darkMode ? 'border-slate-700/60' : 'border-slate-200/70'

  return (
    <div
      className={cn(
        'rounded-xl border overflow-hidden',
        darkMode ? 'border-slate-700 bg-slate-800/40' : 'border-slate-200 bg-white',
      )}
    >
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead className={cn(darkMode ? 'bg-slate-800/80' : 'bg-slate-50')}>
            <tr className={cn('border-b', rowBorder)}>
              <th className={cn(headerBase, 'pl-4 w-12')}></th>
              <th className={headerBase}>
                {t('manager.analytics.drilldown.appts.startTime')}
              </th>
              <th className={headerBase}>
                {t('manager.analytics.drilldown.appts.client')}
              </th>
              <th className={headerBase}>
                {t('manager.analytics.drilldown.appts.creator')}
              </th>
              <th className={headerBase}>
                {t('manager.analytics.drilldown.appts.type')}
              </th>
              <th className={cn(headerBase, 'text-right pr-4')}>
                {t('manager.analytics.drilldown.appts.status')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className={cn(
                  'border-b last:border-b-0 transition-colors',
                  rowBorder,
                  darkMode ? 'hover:bg-slate-800/60' : 'hover:bg-slate-50',
                )}
              >
                <td className={cn(cellBase, 'pl-4')}>
                  <Calendar className="h-4 w-4 text-indigo-500" />
                </td>
                <td className={cn(cellBase, 'tabular-nums')}>
                  {formatDateTime(row.start_time, locale)}
                </td>
                <td className={cellBase}>
                  <div className="flex flex-col">
                    <span className={cn(darkMode ? 'text-white' : 'text-slate-900', 'font-medium')}>
                      {row.client_name}
                    </span>
                    {row.client_phone && (
                      <span className={cn('text-xs', darkMode ? 'text-slate-500' : 'text-slate-400')}>
                        {row.client_phone}
                      </span>
                    )}
                  </div>
                </td>
                <td className={cellBase}>
                  {row.created_by?.name ?? (
                    <span className={cn(darkMode ? 'text-slate-500' : 'text-slate-400')}>
                      {t('manager.analytics.drilldown.appts.byAi')}
                    </span>
                  )}
                </td>
                <td className={cn(cellBase, 'text-xs capitalize')}>
                  {row.appointment_type}
                </td>
                <td className={cn(cellBase, 'text-right pr-4')}>
                  <span
                    className={cn(
                      'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                      STATUS_TONE[row.status] ||
                        'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
                    )}
                  >
                    {row.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
